import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import {
  PLACEMENT_FILTER_CONSTANTS,
  PlacementFilterConstants,
} from '../../constants/placement-filters.constants';
import { StudentProfile } from '../../interfaces/student';
import * as _ from 'lodash';
import {
  getReferenceDataById,
  getReferenceDataByValue,
} from '../../functions/get-reference-data-by-id';
import { SchoolSearchCriteria } from 'src/app/modules/placement/models/placement-finder/school-search-criteria';
import { ActionType } from '../../enums/action-type';
import { PlacementFiltersReferenceData } from '../../interfaces/placement-filters-refernce';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { PlacementFilterFormValue } from '../../interfaces/placement-filter-form-value';

@Component({
    selector: 'app-placement-filters',
    templateUrl: './placement-filters.component.html',
    styleUrls: ['./placement-filters.component.scss'],
    standalone: false
})
export class PlacementFiltersComponent
  extends DialogContentBase
  implements OnInit
{
  form: FormGroup;
  constants: PlacementFilterConstants = PLACEMENT_FILTER_CONSTANTS;
  studentDetails: StudentProfile;
  iepFormSk: string;
  referenceData: PlacementFiltersReferenceData;
  defaultFilters: SchoolSearchCriteria;
  previousFilters: SchoolSearchCriteria;
  isSummer: boolean = false;
  schoolYears: IReferenceData[] = [];
  currentAction: 'select' | 'deselect' | null = null;
  readonly groupSiteTypes: string = 'siteTypes';
  readonly ActionType: typeof ActionType = ActionType;
  private specializedProgramSource: IReferenceData[];
  private filteredSpecializedPrograms: IReferenceData[] = [];

  get specializedProgramOptions(): IReferenceData[] {
    const options = this.referenceData?.specializedProgram ?? [];
    if (options !== this.specializedProgramSource) {
      this.specializedProgramSource = options;
      this.filteredSpecializedPrograms = options.filter(
        (option: IReferenceData) =>
          option.description?.trim().toLowerCase() !== 'not specified'
      );
    }
    return this.filteredSpecializedPrograms;
  }

  constructor(
    private fb: FormBuilder,
    dialog: DialogRef,
    library: FaIconLibrary
  ) {
    super(dialog);
    library.addIcons(faXmark);
  }
  ngOnInit(): void {
    this.initializeForm(
      this.previousFilters ? this.previousFilters : this.defaultFilters
    );
  }

  initializeForm(filters: SchoolSearchCriteria): void {
    let initialLanguage: string[] = [];
    initialLanguage = filters?.scClassLanguage || [];

    this.form = this.fb.group({
      showByAvailableSeats: [filters?.showByAvailableSeats],
      isAccessibleSchoolRequired: [filters?.isAccessibleSchoolRequired],
      language: [initialLanguage],
      program: [filters?.scscis],
      specializedProgram: [filters?.specializedProgram],
      ratio: [filters?.scSpecialRatio],
      preSchoolType: [filters?.preSchoolType],
      district: [
        getReferenceDataById(
          Number(filters?.district),
          this.referenceData?.district
        ),
      ],
      borough: [
        getReferenceDataByValue(filters?.borough, this.referenceData?.borough),
      ],
      schoolYear: [
        this.schoolYears.find(
          (year: IReferenceData) => year.value === filters?.schoolYear
        ),
      ],
    });

    this.updateInstructionModelState();
    this.form.get('program')?.valueChanges.subscribe(() => {
      this.updateInstructionModelState();
    });
  }

  private updateInstructionModelState(): void {
    const selectedPrograms: string[] = this.form?.get('program')?.value ?? [];
    const programSelected: boolean = !!selectedPrograms.length;
    const specializedProgramControl = this.form?.get('specializedProgram');
    if (programSelected) {
      specializedProgramControl?.enable({ emitEvent: false });
    } else {
      specializedProgramControl?.setValue([], { emitEvent: false });
      specializedProgramControl?.disable({ emitEvent: false });
    }

    if (!selectedPrograms.includes('SC')) {
      this.form?.get('ratio')?.setValue([], { emitEvent: false });
    }
  }

  public close(searchObj: SchoolSearchCriteria): void {
    this.dialog.close(searchObj);
  }

  toggleSelectAll(select: boolean): void {
    if (select && this.currentAction !== ActionType.Select) {
      this.onCheckboxChange(true, this.groupSiteTypes);
      this.currentAction = ActionType.Select;
    } else if (!select && this.currentAction !== ActionType.Deselect) {
      this.onCheckboxChange(false, this.groupSiteTypes);
      this.currentAction = ActionType.Deselect;
    }
  }

  onCheckboxChange(event: boolean, type?: string): void {
    const CONTROLS: string[] = _.map(this.constants[type], 'control');
    CONTROLS.forEach((ctrl: string) => {
      this.form.get(ctrl).patchValue(event);
    });
  }

  onShowresults(): void {
    const VAL: PlacementFilterFormValue = this.form.value;
    const selectedPrograms: string[] = VAL?.program ?? [];
    const SEARCHOBJ: SchoolSearchCriteria = {
      scscis: this.form?.value?.program,
      scClassLanguage: VAL?.language?.length ? VAL.language : [],
      scSpecialRatio: selectedPrograms.includes('SC') ? VAL?.ratio ?? [] : [],
      showByAvailableSeats: this.form.value.showByAvailableSeats,
      restrictStudentsDistrict: this.form?.value?.restrictStudentsDistrict,
      homeDistrict: Number(this.studentDetails?.homeDistrict),
      isAccessibleSchoolRequired: this.form?.value?.isAccessibleSchoolRequired,
      preSchoolType: this.form?.value?.preSchoolType?.description,
      district: this.form?.value?.district?.id,
      specializedProgram: this.form?.value?.specializedProgram,
      borough: this.form?.value?.borough?.value,
      isSummer: this.isSummer,
      schoolYear: this.form?.value?.schoolYear?.value,
    };
    this.close(SEARCHOBJ);
  }

  resetFilters(): void {
    this.close(this.defaultFilters);
  }
}
